import { isDefined } from "@clipboard-health/util-ts";
import { BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction";
import { getUnmetCriteriaWithMetadata } from "@src/appV2/OpenShifts/ShiftAction/getUnmetCriteriaWithMetadata";
import { hasUnmetBookabilityCriteria } from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import {
  type BackgroundCheckMissingDataType,
  BookabilityDecision,
  type BookabilityStatusItem,
} from "@src/appV2/OpenShifts/ShiftAction/types";

export interface BackgroundCheckContext {
  isMissingData: boolean;
  isBlockedOnlyDueToMissingData: boolean;
  isRejected: boolean;
  missingDataTypes: BackgroundCheckMissingDataType[];
}

/**
 * Returns context about background check based on the bookability status
 *
 * @param bookabilityStatus - The bookability status to get the background check context for
 * @returns The background check context
 * @description
 * - isMissingData: If booking is blocked because the worker is missing data for background check, this will be true
 * - isBlockedOnlyDueToMissingData: If booking is blocked only because the worker is missing data for background check, this will be true
 * - isRejected: If booking is blocked because the worker's background check was rejected, this will be true
 * - missingDataTypes: The types of data that the worker is missing for background check. This is used to determine
 * which data to collect for the background check.
 */
export function getBackgroundCheckContext(
  bookabilityStatus: BookabilityStatusItem | undefined
): BackgroundCheckContext {
  const defaultContext: BackgroundCheckContext = {
    isMissingData: false,
    isBlockedOnlyDueToMissingData: false,
    isRejected: false,
    missingDataTypes: [],
  };
  if (!isDefined(bookabilityStatus)) {
    return defaultContext;
  }

  const isRejected = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_HAS_REJECTED_BACKGROUND_CHECK,
  ]);
  const isMissingData = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK,
  ]);
  const isBlockedDueToOnlyOneUnmetCriteria =
    bookabilityStatus.attributes.bookability.decision === BookabilityDecision.BLOCKED &&
    bookabilityStatus.attributes.bookability.unmetCriteria.length === 1;

  const unmetCriteriaWithMetadata = getUnmetCriteriaWithMetadata({
    bookabilityStatus,
    unmetCriteria: BookabilityUnmetCriteria.WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK,
  });
  const missingDataTypes = unmetCriteriaWithMetadata?.metadata.missingDataTypes ?? [];

  return {
    isRejected,
    isMissingData,
    isBlockedOnlyDueToMissingData: isMissingData && isBlockedDueToOnlyOneUnmetCriteria,
    missingDataTypes,
  };
}
